import React, { useState, useMemo, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import {
  buildExerciseAutocompleteSuggestions,
  checkForDuplicates,
} from "../../utils/exerciseSimilarity";
import { useExercisesBigQuery } from "../../hooks/useExercisesBigQuery";
import {
  DUPLICATE_WARNING_AUTOCOMPLETE_SX,
  DUPLICATE_WARNING_BOX_SX,
} from "./exerciseDuplicateWarningStyles";

/**
 * ExerciseSearchAutocomplete - Shared component for exercise search with similarity matching
 * 
 * Features:
 * - Unified fuzzy matching with Levenshtein distance
 * - Percentage match scores displayed for prefix and fuzzy matches
 * - Duplicate detection warnings
 * - Exercise creation support
 * - Consistent UX across all exercise search interfaces
 * 
 * @param {Object} props
 * @param {Function} props.onChange - Callback when exercise is selected: (exerciseId, exerciseName) => void
 * @param {Function} props.onCreateNew - Optional callback when creating new exercise: (exerciseName) => Promise<exerciseId>
 * @param {string} props.value - Currently selected exercise ID
 * @param {string} props.label - Label for the input field (default: "Select or Create Exercise")
 * @param {string} props.placeholder - Placeholder text (default: "Start typing to search or create new exercise...")
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {boolean} props.loading - External loading state (optional, will use internal if not provided)
 * @param {number} props.limit - Number of exercises to fetch (default: 10000)
 * @param {string|null} props.organizationId - Optional org id (includes org-scoped custom exercises in catalog)
 * @param {boolean} props.enabled - Whether to fetch exercises (default: true, only used if exercises not provided)
 * @param {Array} props.exercises - Optional pre-fetched exercises array (if provided, skips internal fetching)
 * @param {Object} props.sx - Additional Material-UI sx styles
 * @param {string} props.size - Size of the input ("small" | "medium")
 * @param {boolean} props.fullWidth - Whether input should be full width
 * @param {boolean} props.autoFocus - Whether to auto-focus the input
 */
const ExerciseSearchAutocomplete = ({
  onChange,
  onCreateNew,
  value = null,
  label = "Select or Create Exercise",
  placeholder = "Start typing to search or create new exercise...",
  disabled = false,
  loading: externalLoading = null,
  limit = 10000,
  organizationId = null,
  enabled = true,
  exercises: providedExercises = null,
  sx = {},
  size = "medium",
  fullWidth = true,
  autoFocus = false,
  helperText = null,
}) => {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isCreatingExercise, setIsCreatingExercise] = useState(false);

  // Fetch exercises from BigQuery only if not provided
  const {
    data: exercisesData,
    isLoading: isLoadingExercises,
  } = useExercisesBigQuery({
    search: "",
    type: "all",
    limit,
    offset: 0,
    organization_id: organizationId,
    enabled: enabled && !providedExercises, // Only fetch if exercises not provided
  });

  // Use provided exercises or fetch internally
  const availableExercises = providedExercises || exercisesData?.data?.exercises || [];
  const isLoading = externalLoading !== null ? externalLoading : (providedExercises ? false : isLoadingExercises);
  const isSearching = isLoading;

  const resolvedHelperText =
    helperText !== null
      ? helperText
      : isCreatingExercise
        ? "Creating exercise… please wait."
        : isSearching
          ? "Searching for exercises..."
          : "";

  // Find currently selected exercise
  const currentExercise = useMemo(() => {
    if (!value) return null;
    return availableExercises.find((ex) => ex.id === value) || null;
  }, [value, availableExercises]);

  // Filter and score exercises based on search input
  // This function is called by Material-UI's Autocomplete with the current inputValue
  const filterAndScoreOptions = useCallback(
    (options, { inputValue }) => {
      if (!inputValue || inputValue.trim().length === 0) {
        return []; // Don't show any options until user starts typing
      }

      const { suggestions, hasExactNameMatch } =
        buildExerciseAutocompleteSuggestions(options, inputValue, {
          maxResults: 20,
        });

      const filtered = [...suggestions];

      const duplicateCheck = checkForDuplicates(inputValue, options, 0.85);

      if (inputValue && !hasExactNameMatch) {
        if (duplicateCheck.isDuplicate) {
          filtered.unshift({
            id: "similar-warning",
            name: `⚠️ Similar exercises found - check options below`,
            isWarning: true,
            originalName: inputValue,
          });
        }

        if (onCreateNew) {
          filtered.push({
            id: "create-new",
            name: `Create "${inputValue}"`,
            isCreateNew: true,
            originalName: inputValue,
          });
        }
      }

      return filtered;
    },
    [onCreateNew],
  );

  const handleChange = useCallback(
    async (event, newValue) => {
      // Prevent selection of warning messages
      if (newValue && newValue.isWarning) {
        return; // Don't set warning as selected value
      }

      if (newValue && newValue.isCreateNew && !onCreateNew) {
        return;
      }

      // Handle "Create new" option
      if (newValue && newValue.isCreateNew && onCreateNew) {
        try {
          setIsCreatingExercise(true);
          const exerciseName = newValue.originalName || "";
          const exerciseId = await onCreateNew(exerciseName);

          if (exerciseId && onChange) {
            onChange(exerciseId, exerciseName);
            setSelectedExercise({
              id: exerciseId,
              name: exerciseName,
            });
          }
        } catch (error) {
          console.error("Failed to create exercise:", error);
        } finally {
          setIsCreatingExercise(false);
        }
        return;
      }

      // Handle selection of existing exercise
      if (newValue && typeof newValue === "object" && newValue.id) {
        setSelectedExercise(newValue);
        if (onChange) {
          onChange(newValue.id, newValue.name);
        }
      } else if (typeof newValue === "string" && newValue.trim()) {
        // User typed a custom name (freeSolo mode)
        // If onCreateNew is provided, create the exercise
        if (onCreateNew) {
          try {
            setIsCreatingExercise(true);
            const exerciseId = await onCreateNew(newValue.trim());
            if (exerciseId && onChange) {
              onChange(exerciseId, newValue.trim());
            }
          } catch (error) {
            console.error("Failed to create exercise:", error);
          } finally {
            setIsCreatingExercise(false);
          }
        } else {
          // Just set the value as-is if no create handler
          setSelectedExercise(newValue);
        }
      } else {
        // Clear selection
        setSelectedExercise(null);
        if (onChange) {
          onChange(null, null);
        }
      }
    },
    [onChange, onCreateNew]
  );

  return (
    <Box sx={{ width: fullWidth ? "100%" : "auto" }}>
      {isCreatingExercise && (
        <LinearProgress
          sx={{ mb: 1, borderRadius: 1 }}
          aria-label="Creating exercise"
        />
      )}
    <Autocomplete
        options={availableExercises}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return option.name || option.label || "";
        }}
        value={currentExercise || selectedExercise}
        disabled={disabled || isCreatingExercise}
        onChange={handleChange}
        getOptionDisabled={(option) => option.isWarning || false}
        freeSolo={!!onCreateNew} // Allow custom input if onCreateNew is provided
        loading={isSearching}
        loadingText="Searching exercises..."
        noOptionsText={
          isSearching
            ? "Searching..."
            : "Start typing to search exercises..."
        }
        filterOptions={filterAndScoreOptions}
        sx={{ ...DUPLICATE_WARNING_AUTOCOMPLETE_SX, ...sx }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            fullWidth={fullWidth}
            size={size}
            autoFocus={autoFocus}
            disabled={disabled || isCreatingExercise}
            helperText={resolvedHelperText}
            inputProps={{
              ...params.inputProps,
              "aria-busy": isCreatingExercise || isSearching,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isCreatingExercise && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} sx={{ mr: 1 }} aria-label="Creating exercise" />
                    </InputAdornment>
                  )}
                  {!isCreatingExercise && isSearching && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    </InputAdornment>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.id || option.name || option}
            data-duplicate-warning={option.isWarning ? "true" : undefined}
          >
            {option.isWarning ? (
              <Box sx={DUPLICATE_WARNING_BOX_SX}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "inherit", color: "inherit" }}
                >
                  {option.name || ""}
                </Typography>
              </Box>
            ) : option.isCreateNew ? (
              <Box sx={{ fontStyle: "italic", color: "primary.main" }}>
                {option.name || option.originalName || ""}
              </Box>
            ) : (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Typography variant="body2">
                  {typeof option === "string" ? option : (option.name || "")}
                </Typography>
                {option.isSimilar && option.similarity && (
                  <Chip
                    label={`${Math.round(option.similarity * 100)}% match`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 18 }}
                  />
                )}
              </Box>
            )}
          </li>
        )}
        autoHighlight
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          if (typeof option === "string" || typeof value === "string") {
            return option === value;
          }
        return option.id === value.id;
      }}
    />
    </Box>
  );
};

export default ExerciseSearchAutocomplete;
